import React from 'react';
import { MessageSquare, Zap, Clock, Globe } from 'lucide-react';
import { LeadFormModal } from '../components/LeadFormModal';
import { usePageMetadata } from '../hooks/usePageMetadata';
import { CaseStudyLink } from '../components/CaseStudyLink';
import { CASE_STUDIES } from '../data/caseStudies';
import { Breadcrumb } from '../components/Breadcrumb';
import { WhatNextSection } from '../components/WhatNextSection';
import { ParticlesBackground } from '../components/effects/ParticlesBackground';
import { AnimatedBlob } from '../components/effects/AnimatedBlob';
import { AnimatedGrid } from '../components/effects/AnimatedGrid';
import { RevealOnScroll } from '../components/effects/RevealOnScroll';
import { GlassCard } from '../components/ui/GlassCard';
import { FeatureCard } from '../components/ui/FeatureCard';
import { StepCard } from '../components/ui/StepCard';

export const AgentWhatsAppAiPage: React.FC = () => {
    const relatedCaseStudy = CASE_STUDIES.find(cs => cs.id === 'sklep-ecommerce');

    usePageMetadata({
        title: "Agent AI WhatsApp | Automatyzacja Obsługi Klienta 24/7",
        description: "Zautomatyzuj obsługę na WhatsApp i Messenger. Agent AI odpowiada na pytania, przyjmuje zamówienia i rozwiązuje problemy 24/7. Sprawdź nasze demo.",
        schema: [
            {
                "@context": "https://schema.org",
                "@type": "Product",
                "name": "Agent WhatsApp & Messenger AI",
                "description": "Inteligentny asystent AI dla WhatsApp i Messenger, który pracuje 24/7, odpowiada na pytania klientów, przyjmuje zamówienia i automatyzuje procesy sprzedażowe.",
                "brand": {
                    "@type": "Brand",
                    "name": "BiznesBezKlikania.pl"
                },
                "manufacturer": {
                    "@type": "Organization",
                    "name": "BiznesBezKlikania.pl",
                    "url": "https://biznesbezklikania.pl"
                },
                "category": "Software > AI Automation",
                "url": "https://biznesbezklikania.pl/rozwiazania/agent-whatsapp-ai",
                "image": "https://biznesbezklikania.pl/og-image.png",
                "offers": {
                    "@type": "Offer",
                    "availability": "https://schema.org/InStock",
                    "priceSpecification": {
                        "@type": "PriceSpecification",
                        "priceCurrency": "PLN"
                    },
                    "seller": {
                        "@type": "Organization",
                        "name": "BiznesBezKlikania.pl"
                    }
                },
                "aggregateRating": {
                    "@type": "AggregateRating",
                    "ratingValue": "4.9",
                    "reviewCount": "47",
                    "bestRating": "5"
                }
            },
            {
                "@context": "https://schema.org",
                "@type": "SoftwareApplication",
                "name": "Agent WhatsApp & Messenger AI",
                "applicationCategory": "BusinessApplication",
                "operatingSystem": "Cloud-based",
                "offers": {
                    "@type": "Offer",
                    "availability": "https://schema.org/InStock"
                }
            }
        ],
        ogData: {
            type: 'product'
        }
    });

    const steps = [
        { number: 1, title: 'Analiza i strategia', description: 'Określamy kluczowe pytania klientów i cele, jakie ma realizować AI (np. sprzedaż, umawianie spotkań).' },
        { number: 2, title: 'Konfiguracja AI', description: 'Uczymy bota odpowiedzi na podstawie Twojej bazy wiedzy, integrujemy go z Messengerem i WhatsApp.' },
        { number: 3, title: 'Start i optymalizacja', description: 'Uruchamiamy asystenta. Monitorujemy jego pracę i na bieżąco ulepszamy, aby był jeszcze skuteczniejszy.' },
    ];

    const features = [
        { icon: <Zap className="w-8 h-8" />, title: 'Błyskawiczne odpowiedzi', description: 'Odpowiadaj na zapytania klientów w mniej niż 5 sekund, 24 godziny na dobę, 7 dni w tygodniu.' },
        { icon: <Clock className="w-8 h-8" />, title: 'Obsługa 24/7', description: 'Twój wirtualny pracownik nigdy nie śpi, obsługując klientów nawet w środku nocy i w święta.' },
        { icon: <MessageSquare className="w-8 h-8" />, title: 'Rekomendacje AI', description: 'Analizuj preferencje klienta i proponuj produkty dopasowane do jego potrzeb, zwiększając wartość koszyka.' },
        { icon: <Globe className="w-8 h-8" />, title: 'Wielojęzyczność', description: 'Obsługuj klientów z całego świata w ponad 95 językach bez dodatkowych kosztów.' },
    ];

    return (
        <>
            {/* Hero Section */}
            <section className="relative min-h-[70vh] flex items-center overflow-hidden py-20 md:py-24">
                <ParticlesBackground variant="default" />
                <AnimatedBlob variant="blue" />
                <div className="absolute inset-0 bg-gradient-to-b from-transparent via-bkg-light/50 to-bkg-light dark:via-bkg-dark/50 dark:to-bkg-dark pointer-events-none" />

                <div className="container mx-auto px-4 relative z-10">
                    <div className="grid lg:grid-cols-2 gap-12 items-center">
                        <div>
                            <RevealOnScroll>
                                <p className="text-sm font-semibold text-primary-light dark:text-primary-dark uppercase tracking-wider mb-4">
                                    Automatyzacja obsługi klienta
                                </p>
                                <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold mb-6 tracking-tight bg-gradient-to-r from-primary-light via-secondary-light to-primary-light dark:from-primary-dark dark:via-secondary-dark dark:to-primary-dark bg-clip-text text-transparent bg-[length:200%_200%] animate-gradient">
                                    Agent WhatsApp & Messenger AI
                                </h1>
                            </RevealOnScroll>

                            <RevealOnScroll delay={100}>
                                <p className="text-lg md:text-xl text-text-secondary-light dark:text-text-secondary-dark mb-8">
                                    Inteligentny asystent, który obsługuje zamówienia, odpowiada na pytania o produkty i śledzi przesyłki. Pracuje 24/7 bez przerw i urlopów.
                                </p>
                            </RevealOnScroll>

                            <RevealOnScroll delay={200}>
                                <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
                                    <LeadFormModal
                                        productSlug="agent-whatsapp-ai"
                                        triggerLabel="Umów bezpłatne demo"
                                        triggerClassName="px-6 py-3 w-full sm:w-auto justify-center font-semibold bg-white/10 dark:bg-white/5 backdrop-blur-sm border border-white/20 dark:border-white/10 text-primary-light dark:text-primary-dark rounded-xl hover:bg-white/20 dark:hover:bg-white/10 hover:scale-[1.02] transition-all duration-300 text-lg inline-flex"
                                    />
                                    <LeadFormModal
                                        productSlug="agent-whatsapp-ai"
                                        triggerLabel="Zleć wdrożenie"
                                        triggerClassName="px-6 py-3 w-full sm:w-auto justify-center font-semibold text-white bg-gradient-to-r from-primary-light to-secondary-light dark:from-primary-dark dark:to-secondary-dark rounded-xl hover:shadow-xl hover:shadow-primary-light/30 dark:hover:shadow-primary-dark/30 hover:scale-[1.02] transition-all duration-300 text-lg inline-flex items-center group animate-glow"
                                    />
                                </div>
                            </RevealOnScroll>
                        </div>

                        {/* Chat Mockup with Glassmorphism */}
                        <RevealOnScroll delay={300} direction="right">
                            <GlassCard variant="strong" className="p-0 overflow-hidden">
                                <div className="bg-gradient-to-r from-primary-light to-secondary-light dark:from-primary-dark dark:to-secondary-dark text-white p-4">
                                    <div className="flex items-center gap-3">
                                        <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
                                            <MessageSquare className="w-5 h-5" />
                                        </div>
                                        <div>
                                            <p className="font-semibold">Asystent AI Sklep.pl</p>
                                            <p className="text-xs text-white/80 flex items-center gap-1">
                                                <span className="w-2 h-2 rounded-full bg-green-400 animate-pulse" />
                                                Online
                                            </p>
                                        </div>
                                    </div>
                                </div>
                                <div className="p-4 space-y-4 h-72 overflow-y-auto bg-white/50 dark:bg-white/5 backdrop-blur-sm">
                                    <div className="p-3 bg-white/80 dark:bg-white/10 rounded-2xl rounded-bl-sm max-w-[80%] shadow-sm">
                                        <p className="text-text-primary-light dark:text-text-primary-dark text-sm">
                                            Cześć! Jestem Asystentem AI. W czym mogę pomóc? 😊
                                        </p>
                                    </div>
                                    <div className="ml-auto p-3 bg-gradient-to-r from-primary-light to-secondary-light dark:from-primary-dark dark:to-secondary-dark text-white rounded-2xl rounded-br-sm max-w-[80%] shadow-sm">
                                        <p className="text-sm">Szukam laptopa do 3000 zł</p>
                                    </div>
                                    <div className="p-3 bg-white/80 dark:bg-white/10 rounded-2xl rounded-bl-sm max-w-[80%] shadow-sm">
                                        <p className="text-text-primary-light dark:text-text-primary-dark text-sm">
                                            Świetnie! Polecam 3 modele w Twoim budżecie. Który parametr jest dla Ciebie najważniejszy: wydajność, czas pracy na baterii, czy waga?
                                        </p>
                                    </div>
                                </div>
                            </GlassCard>
                        </RevealOnScroll>
                    </div>
                </div>

                <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-bkg-light dark:from-bkg-dark to-transparent pointer-events-none" />
            </section>

            <Breadcrumb />

            {/* How It Works Section */}
            <section className="py-16 md:py-20 relative">
                <AnimatedGrid variant="matrix" animated={false} />
                <div className="container mx-auto px-4 relative z-10">
                    <RevealOnScroll className="text-center mb-12">
                        <h2 className="text-3xl md:text-4xl font-bold text-text-primary-light dark:text-text-primary-dark">
                            Jak to działa? Wdrożenie w 3 krokach.
                        </h2>
                        <p className="text-lg text-text-secondary-light dark:text-text-secondary-dark mt-4 max-w-2xl mx-auto">
                            Prosty i szybki proces, aby Twój asystent zaczął działać nawet w 48 godzin.
                        </p>
                    </RevealOnScroll>

                    <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
                        {steps.map((step, index) => (
                            <RevealOnScroll key={step.number} delay={index * 150}>
                                <StepCard
                                    number={step.number}
                                    title={step.title}
                                    description={step.description}
                                    animationDelay={index * 200}
                                />
                            </RevealOnScroll>
                        ))}
                    </div>
                </div>
            </section>

            {/* Features Section */}
            <section className="py-16 md:py-20 relative bg-content-light/50 dark:bg-content-dark/50">
                <AnimatedGrid variant="dots" animated={false} />
                <div className="container mx-auto px-4 relative z-10">
                    <RevealOnScroll className="text-center mb-12">
                        <h2 className="text-3xl md:text-4xl font-bold text-text-primary-light dark:text-text-primary-dark">
                            Dlaczego Agent AI to Twój najlepszy pracownik?
                        </h2>
                        <p className="text-lg text-text-secondary-light dark:text-text-secondary-dark mt-4 max-w-2xl mx-auto">
                            Kompletne rozwiązanie automatyzujące interakcje z klientami i zwiększające sprzedaż.
                        </p>
                    </RevealOnScroll>

                    <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
                        {features.map((feature, index) => (
                            <RevealOnScroll key={feature.title} delay={index * 100}>
                                <FeatureCard
                                    icon={feature.icon}
                                    title={feature.title}
                                    description={feature.description}
                                />
                            </RevealOnScroll>
                        ))}
                    </div>
                </div>
            </section>

            {relatedCaseStudy && <CaseStudyLink caseStudy={relatedCaseStudy} />}
            <WhatNextSection diyLink="/rozwiazania/agent-whatsapp-ai" />
        </>
    );
};
